/*
  Lior Apple layer: motion and detail sheets for sections/lior-apple-promise.liquid.
  - The headline and each tile wait (.lpr-pre) until they come into view, then
    reveal (.is-in). Tiles that arrive together are staggered through --d.
  - Digits in a tile's big number count up from zero as the tile is revealed.
  - .is-visible on the section runs the idle loops (aurora, typing dots, speed lines).
  - "+" (or a click anywhere on the tile) opens its <dialog> sheet. Esc, the close
    button, a click outside or a swipe down on phones closes it; focus goes back.
  - Phones, with the section's row on ([data-lar], assets/lior-apple-row-carousel.js):
    the cards of the row come in together when the row comes into view, one
    after another (.lpr-row-pre → .lpr-row-in on the section), and a card sinks
    under the finger (.is-pressed) after a beat, so a swipe does not bob it.
  Content that is already on screen is never hidden, and nothing animates in the
  theme editor or with reduced motion.
*/
(function () {
  'use strict';

  if (window.__liorPromise) return;
  window.__liorPromise = true;

  var doc = document;
  var win = window;
  var mq = function (q) { return win.matchMedia ? win.matchMedia(q) : { matches: false }; };
  var reduce = mq('(prefers-reduced-motion: reduce)');
  var phone = mq('(max-width: 767px)');
  var designMode = !!(win.Shopify && win.Shopify.designMode);
  var hasIO = 'IntersectionObserver' in win;
  var rtl = (doc.documentElement.getAttribute('dir') || '').toLowerCase() === 'rtl';

  function inView(el, frac) {
    var r = el.getBoundingClientRect();
    var vh = win.innerHeight || doc.documentElement.clientHeight;
    return r.top < vh * frac && r.bottom > 0;
  }

  /*
   * Numbers that count up
   */
  function countUp(el, delay) {
    var finalText = el.getAttribute('data-final');
    if (finalText === null) {
      finalText = el.textContent;
      el.setAttribute('data-final', finalText);
    }
    var nums = finalText.match(/\d+/g);
    if (!nums) return;
    var targets = nums.map(Number);
    var biggest = Math.max.apply(null, targets);
    var duration = 950 + Math.min(650, biggest * 6);

    /* Hold the final width so the words beside the number do not shuffle */
    el.style.minWidth = (el.offsetWidth + 1) + 'px';
    el.style.textAlign = rtl ? 'right' : 'left';

    function render(k) {
      var i = 0;
      el.textContent = finalText.replace(/\d+/g, function () {
        return String(Math.round(targets[i++] * k));
      });
    }

    render(0);
    var start = null;
    function frame(now) {
      if (start === null) start = now;
      var p = Math.min(1, (now - start) / duration);
      render(1 - Math.pow(1 - p, 3));
      if (p < 1) {
        requestAnimationFrame(frame);
      } else {
        el.textContent = finalText;
        el.style.minWidth = '';
        el.style.textAlign = '';
      }
    }
    setTimeout(function () { requestAnimationFrame(frame); }, delay);
  }

  /*
   * Reveal on first view
   */
  function reveal(el, delay) {
    el.style.setProperty('--d', delay + 'ms');
    var num = el.querySelector('.lpr-tile [data-lpr-count]');
    if (num) countUp(num, delay + 320);
    requestAnimationFrame(function () { el.classList.add('is-in'); });
  }

  var revealIO = hasIO ? new IntersectionObserver(function (entries) {
    var batch = [];
    for (var i = 0; i < entries.length; i++) {
      if (!entries[i].isIntersecting) continue;
      revealIO.unobserve(entries[i].target);
      batch.push(entries[i].target);
    }
    batch.sort(function (a, b) {
      return a.compareDocumentPosition(b) & Node.DOCUMENT_POSITION_FOLLOWING ? -1 : 1;
    });
    /* Tiles that arrive with the headline wait for it to start first */
    var base = 0;
    var n = 0;
    for (var k = 0; k < batch.length; k++) {
      var el = batch[k];
      if (el.hasAttribute('data-lpr-head')) {
        reveal(el, 0);
        base = 340;
      } else if (el.hasAttribute('data-lar-track')) {
        /* A phone row: its cards come in one after another */
        var cards = el.children;
        var at = base + n * 110;
        for (var c = 0; c < cards.length; c++) {
          if (cards[c].classList.contains('lpr-pre')) reveal(cards[c], at + c * 90);
        }
        var root = el.closest('[data-lpr]');
        if (root) {
          root.style.setProperty('--lar-pd', (at + 380) + 'ms');
          root.classList.add('lpr-row-in');
        }
        n++;
      } else {
        reveal(el, base + n * 110);
        n++;
      }
    }
  }, { threshold: 0.18, rootMargin: '0px 0px -6% 0px' }) : null;

  var visibleIO = hasIO ? new IntersectionObserver(function (entries) {
    for (var i = 0; i < entries.length; i++) {
      entries[i].target.classList.toggle('is-visible', entries[i].isIntersecting);
    }
  }) : null;

  function setup(root) {
    if (root.__lprReady) return;
    root.__lprReady = true;

    if (visibleIO) visibleIO.observe(root);
    else root.classList.add('is-visible');

    if (designMode || reduce.matches || !hasIO) return;

    var waiting = [];
    var head = root.querySelector('[data-lpr-head]');
    if (head) waiting.push(head);
    var items = root.querySelectorAll('.lpr-item');
    var track = root.querySelector('[data-lar-track]');

    /* Phones, one row: the row is watched, not each card (the cards off to
       the side would otherwise wait, hidden, until they are swiped in) */
    if (track && root.hasAttribute('data-lar') && phone.matches) {
      if (head && !inView(head, 0.92)) {
        head.classList.add('lpr-pre');
        revealIO.observe(head);
      }
      if (!inView(track, 0.92)) {
        for (var r = 0; r < items.length; r++) items[r].classList.add('lpr-pre');
        root.classList.add('lpr-row-pre');
        revealIO.observe(track);
      }
      return;
    }

    for (var i = 0; i < items.length; i++) waiting.push(items[i]);

    for (var k = 0; k < waiting.length; k++) {
      var el = waiting[k];
      if (inView(el, 0.92)) continue; /* already in sight: leave it as it is */
      el.classList.add('lpr-pre');
      revealIO.observe(el);
    }
  }

  function scan(scope) {
    var roots = (scope || doc).querySelectorAll('[data-lpr]');
    for (var i = 0; i < roots.length; i++) setup(roots[i]);
  }

  /*
   * Detail sheets
   */
  var opener = null;

  function unlock() {
    if (!doc.querySelector('dialog.lpr-sheet[open]')) doc.documentElement.classList.remove('lpr-lock');
  }

  function openSheet(id, from) {
    var d = doc.getElementById(id);
    if (!d || d.open) return;
    opener = from || null;
    if (typeof d.showModal === 'function') d.showModal();
    else d.setAttribute('open', '');
    doc.documentElement.classList.add('lpr-lock');

    var panel = d.querySelector('.lpr-sheet-panel');
    if (panel) {
      panel.scrollTop = 0;
      panel.style.translate = '';
      /* When the details fit, the whole sheet can be pulled down on phones */
      panel.style.touchAction = panel.scrollHeight <= panel.clientHeight + 1 ? 'none' : '';
    }

    /* Let the closed state paint first, then glide to open */
    requestAnimationFrame(function () {
      requestAnimationFrame(function () { d.classList.add('is-open'); });
    });
  }

  function closeSheet(d) {
    if (!d || !d.open || d.__lprClosing) return;
    d.__lprClosing = true;
    d.classList.remove('is-open');
    var panel = d.querySelector('.lpr-sheet-panel');
    if (panel) {
      panel.classList.remove('is-dragging');
      panel.style.translate = '';
    }

    var finish = function () {
      d.__lprClosing = false;
      if (typeof d.close === 'function') d.close();
      else d.removeAttribute('open');
      unlock();
      if (opener && typeof opener.focus === 'function') {
        try { opener.focus({ preventScroll: true }); } catch (e) { opener.focus(); }
      }
      opener = null;
    };

    if (reduce.matches) finish();
    else setTimeout(finish, 460);
  }

  doc.addEventListener('click', function (e) {
    var t = e.target;
    if (!t || !t.closest) return;

    var closer = t.closest('[data-lpr-close]');
    if (closer) {
      closeSheet(closer.closest('dialog'));
      return;
    }

    /* A click on the dimmed area around the panel */
    if (t.matches('dialog.lpr-sheet')) {
      closeSheet(t);
      return;
    }

    var trigger = t.closest('[data-lpr-open]');
    if (!trigger || t.closest('a') || trigger.closest('dialog')) return;
    e.preventDefault();
    var button = trigger.matches('button') ? trigger : trigger.querySelector('.lpr-plus');
    openSheet(trigger.getAttribute('data-lpr-open'), button);
  });

  /* Esc: animate out instead of vanishing */
  doc.addEventListener('cancel', function (e) {
    var d = e.target;
    if (d && d.matches && d.matches('dialog.lpr-sheet')) {
      e.preventDefault();
      closeSheet(d);
    }
  }, true);

  /* Closed some other way: tidy up */
  doc.addEventListener('close', function (e) {
    var d = e.target;
    if (d && d.matches && d.matches('dialog.lpr-sheet')) {
      d.classList.remove('is-open');
      unlock();
    }
  }, true);

  /* Phones: pull the sheet down to dismiss it */
  doc.addEventListener('pointerdown', function (e) {
    if (!phone.matches || e.pointerType === 'mouse' || reduce.matches) return;
    var t = e.target;
    var panel = t && t.closest ? t.closest('.lpr-sheet-panel') : null;
    if (!panel) return;
    var onGrab = !!t.closest('.lpr-sheet-grab');
    if (!onGrab && (panel.style.touchAction !== 'none' || t.closest('a, button'))) return;

    var d = panel.closest('dialog');
    var y0 = e.clientY;
    var lastY = y0;
    var lastT = e.timeStamp;
    var velocity = 0;
    var dragging = false;

    function move(ev) {
      var dy = ev.clientY - y0;
      if (!dragging) {
        if (Math.abs(dy) < 6) return;
        dragging = true;
        panel.classList.add('is-dragging');
      }
      var dt = Math.max(1, ev.timeStamp - lastT);
      velocity = (ev.clientY - lastY) / dt;
      lastY = ev.clientY;
      lastT = ev.timeStamp;
      var offset = dy > 0 ? dy : dy / 5; /* a little give upward, like iOS */
      panel.style.translate = '0 ' + offset.toFixed(1) + 'px';
    }

    function end(ev) {
      win.removeEventListener('pointermove', move);
      win.removeEventListener('pointerup', end);
      win.removeEventListener('pointercancel', end);
      if (!dragging) return;
      panel.classList.remove('is-dragging');
      var dy = ev.clientY - y0;
      if (dy > 110 || velocity > 0.55) closeSheet(d);
      else panel.style.translate = '';
    }

    win.addEventListener('pointermove', move, { passive: true });
    win.addEventListener('pointerup', end);
    win.addEventListener('pointercancel', end);
  });

  /*
   * Phones, row: the card sinks under the finger. The CSS waits a beat
   * before it sinks, and any movement, a scroll, or the browser taking the
   * touch for a swipe lets it go, so a swipe never bobs the card; a quick
   * tap still shows for a moment.
   */
  var pressed = null;

  function letGo(now) {
    var p = pressed;
    if (!p) return;
    pressed = null;
    var held = Date.now() - p.t;
    var go = function () { p.tile.classList.remove('is-pressed'); };
    if (now || held >= 130) go();
    else setTimeout(go, 130 - held);
  }

  doc.addEventListener('pointerdown', function (e) {
    if (e.button !== 0 || !phone.matches || !e.target || !e.target.closest) return;
    var tile = e.target.closest('[data-lar] .lpr-tile.has-detail');
    if (!tile || e.target.closest('a')) return;
    letGo(true);
    pressed = { tile: tile, x: e.clientX, y: e.clientY, t: Date.now() };
    tile.classList.add('is-pressed');
  }, { passive: true });

  doc.addEventListener('pointermove', function (e) {
    if (pressed && (Math.abs(e.clientX - pressed.x) > 10 || Math.abs(e.clientY - pressed.y) > 10)) letGo(true);
  }, { passive: true });

  doc.addEventListener('pointerup', function () { letGo(false); }, { passive: true });
  doc.addEventListener('pointercancel', function () { letGo(true); }, { passive: true });
  /* capture: the row's own sideways scroll counts too */
  win.addEventListener('scroll', function () { if (pressed) letGo(true); }, { passive: true, capture: true });

  /*
   * Wiring
   */
  if (doc.readyState === 'loading') doc.addEventListener('DOMContentLoaded', function () { scan(); });
  else scan();

  doc.addEventListener('shopify:section:load', function (e) { scan(e.target); });
  doc.addEventListener('shopify:section:unload', function () { setTimeout(unlock, 0); });
})();
